// ShipmentScanningBag.tsx
import { useMutation } from '@tanstack/react-query';
import {
  Block,
  Button,
  FlexContainer,
  FullscreenSheet,
  ScannerInput,
  SoundNotificationContext,
  TableSimple,
  Tabs,
  useNotificationsV2,
} from '@uni-space/uni-components';
import { addBagToShipment } from '../api';
import { useContext, useMemo, useState } from 'react';
import { AddBagToShipmentManifestHttpRequest, AddBagToShipmentStatus, ShipmentStatus } from '../model/shipment';
import { useTranslation } from 'react-i18next';
import { useShipmentStore } from '../model/useShipmentStore';
import { BagDto } from '@features/ManualSorting/model/manualSorting';
import { useShipmentScanningBagSettingsTable } from '../model/useShipmentScanningBagSettingsTable';
import useSHipmentScanningFetch from '../model/useShipmentScanningFetch';

export enum ShipmentScanningBagMenu {
  Add = 'Add',
  Remove = 'Remove',
}

export type ShipmentScanningBagProps = {
  onClose: () => void;
  shipmentStatus: ShipmentStatus;
};

export default function ShipmentScanningBag(props: ShipmentScanningBagProps) {
  const { onClose, shipmentStatus } = props;
  const { currentShipment, bagsData } = useShipmentStore();

  const [scanningMenu, setScanningMenu] = useState<ShipmentScanningBagMenu>(ShipmentScanningBagMenu.Add);
  const { t, i18n } = useTranslation();
  const { createNotification } = useNotificationsV2();
  const { onSoundError } = useContext(SoundNotificationContext);
  const { columns } = useShipmentScanningBagSettingsTable();
  const { isAdding, isDeleting, onAddBag, onDeleteBag } = useSHipmentScanningFetch();

  const handleSubmit = (bagNumber: string, isRemove: boolean = false) => {
    if (!currentShipment) {
      createNotification({
        type: 'error',
        message: {
          title: t('shipmentScanningBag.errors.noShipmentId.title'),
          desc: t('shipmentScanningBag.errors.noShipmentId.desc'),
        },
      });
      onSoundError();
      return;
    }

    const data: AddBagToShipmentManifestHttpRequest = {
      bagNumber,
      shipmentId: currentShipment?.id,
    };

    if (isRemove) {
      onDeleteBag(data);
    } else {
      onAddBag(data);
    }
  };

  const handleChangeTabs = (id: ShipmentScanningBagMenu) => {
    setScanningMenu(id);
  };

  const isShipmentCreatedStatus = useMemo(() => shipmentStatus === ShipmentStatus.Created, [shipmentStatus]);

  return (
    <>
      <FullscreenSheet height={'100dvh'} open={currentShipment !== null} onClose={onClose}>
        {isShipmentCreatedStatus && (
          <FlexContainer justify="space-between" align="center">
            <Block sx={{ maxWidth: 700, flex: 1, marginBottom: 8 }}>
              <Tabs
                value={scanningMenu}
                onChange={(id) => handleChangeTabs(id as ShipmentScanningBagMenu)}
                items={[
                  { id: String(ShipmentScanningBagMenu.Add), label: t('shipmentScanningBag.tabs.add') },
                  { id: String(ShipmentScanningBagMenu.Remove), label: t('shipmentScanningBag.tabs.remove') },
                ]}
              />
            </Block>
            <Block>
              <Button onClick={onClose} width="100px">
                {t('tableHeader.rollUp')}
              </Button>
            </Block>
          </FlexContainer>
        )}
        {scanningMenu === ShipmentScanningBagMenu.Add && isShipmentCreatedStatus && (
          <>
            <ScannerInput
              onSubmit={handleSubmit}
              label={t('shipmentScanningBag.scanner.addLabel')}
              isLoading={isAdding}
              disabled={isAdding}
            />
            <Block mt={16} />
          </>
        )}

        {scanningMenu === ShipmentScanningBagMenu.Remove && isShipmentCreatedStatus && (
          <>
            <ScannerInput
              onSubmit={(number) => handleSubmit(number, true)}
              label={t('shipmentScanningBag.scanner.removeLabel')}
              isLoading={isDeleting}
              disabled={isDeleting}
            />
            <Block mt={16} />
          </>
        )}

        <TableSimple<BagDto>
          enableColumnResizing={false}
          columns={columns}
          data={bagsData}
          language={i18n.language}
          maxHeightTable="calc(100vh - 200px)"
          isLoading={false}
          emptyPlaceholder={t('noData')}
        />
      </FullscreenSheet>
    </>
  );
}
